// The "watch" view for one persistent-agent thread — a full-height chat.
//
// The conversation is one auto-scrolling timeline (prominent user bubbles,
// assistant turns with tool calls grouped, goal/error cards) with a sticky
// composer that carries the kind × delivery semantics (slash commands +
// selector). A drag-resizable right sidebar holds everything that isn't the
// conversation: a pinned status + lifecycle strip, then tabs — Activity (the
// Signals/Events feed), State (facts), Tools, Heartbeat, Watches (sensors + done-when), Mission (checklist), Metrics.
// Each tab owns the full sidebar height and scrolls itself.
import { useClient } from '@cortex/react-query';
import type {
  FormantBot,
  FormantBotUnseen,
  PersistentAgentDetail,
  PersistentAgentEventDelivery,
  PersistentAgentEventKind,
  PersistentAgentNamesResponse,
  PersistentAgentProgressBar,
  PersistentAgentStatus,
} from '@cortex/types';
import {
  formantBotAgentId,
  PersistentAgentOperationStateProjectionSchema,
  PersistentAgentProgressFactSchema,
} from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import {
  buildTimeline,
  collectAgentSourceIds,
  sourceLabel,
  type OptimisticSend,
} from '@formant/chat';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

import { toTimelineCompactions, type CompactionWireEntry } from '../../-thread-compactions';
import {
  displayedThreadStatus,
  resolvedThreadStatus,
  threadLifecyclePath,
  threadResetConfirmLabel,
  threadResetConfirmMessage,
  threadResetConfirmTitle,
  type ThreadLifecycleAction,
} from '../../-thread-lifecycle';
import {
  BotAvatar,
  resolveBotAvatar,
  type AgentAvatarState,
} from '../../../../components/bots/BotAvatar';
import { useBotVoice } from '../../../../components/bots/useBotVoice';
import { BackButton } from '../../../../components/common/BackButton';
import { ConfirmDialog } from '../../../../components/common/ConfirmDialog';
import { Modal } from '../../../../components/common/Modal';
import { PageHeader } from '../../../../components/common/PageHeader';
import { ConsoleAgentChat } from '../../../../components/persistent-agents/ConsoleAgentChat';
import { HeartbeatPanel } from '../../../../components/persistent-agents/HeartbeatPanel';
import { MetricsPanel } from '../../../../components/persistent-agents/MetricsPanel';
import { MissionPanel } from '../../../../components/persistent-agents/MissionPanel';
import { ProgressBar } from '../../../../components/persistent-agents/ProgressBar';
import { SignalsPanel } from '../../../../components/persistent-agents/SignalsPanel';
import { ToolUsagePanel } from '../../../../components/persistent-agents/ToolUsagePanel';
import { ChatComposer } from '../../../../components/persistent-agents/chat/ChatComposer';
import { DreamModal } from '../../../../components/persistent-agents/chat/DreamModal';
import { OperationStartModal } from '../../../../components/persistent-agents/chat/OperationStartModal';
import { PlanProposalModal } from '../../../../components/persistent-agents/chat/PlanProposalModal';
import {
  QueuedTray,
  type QueuedMessage,
} from '../../../../components/persistent-agents/chat/QueuedTray';
import { routes } from '../../../../lib/constants';
import { formatRelative, formatUsd } from '../../../../lib/format';
import {
  usePersistentAgentWatch,
  type IWatchEvent,
  type IWatchFact,
} from '../../../../lib/use-persistent-agent-watch';

export const Route = createFileRoute('/agents/$agentId_/threads/$threadId')({
  component: WatchRoute,
});

function WatchRoute() {
  const { agentId, threadId } = Route.useParams();
  return <WatchPage key={threadId} agentId={agentId} threadId={threadId} />;
}

let localSendSeq = 0;

const sidebarMin = 280;
const sidebarMax = 680;

type SidebarTab =
  | 'activity'
  | 'operation'
  | 'state'
  | 'tools'
  | 'heartbeat'
  | 'signals'
  | 'mission'
  | 'metrics';
const sidebarTabs: { id: SidebarTab; label: string }[] = [
  { id: 'activity', label: 'Activity' },
  { id: 'operation', label: 'Operation' },
  { id: 'state', label: 'State' },
  { id: 'tools', label: 'Tools' },
  { id: 'heartbeat', label: 'Heartbeats' },
  { id: 'signals', label: 'Signals' },
  { id: 'mission', label: 'Mission' },
  { id: 'metrics', label: 'Metrics' },
];

const statusBadge: Record<string, string> = {
  STARTING: 'badge badge--warning',
  IDLE: 'badge badge--neutral',
  WORKING: 'badge badge--success',
  PAUSED: 'badge badge--warning',
  WAITING_FOR_INPUT: 'badge badge--warning',
  STOPPED: 'badge badge--default',
  FAILED: 'badge badge--danger',
};

function botFaceState(
  status: PersistentAgentStatus | null,
  voice: { live: boolean; starting: boolean } | null,
): AgentAvatarState {
  if (voice?.live || voice?.starting) return 'listening';
  if (status === 'FAILED') return 'error';
  if (status === 'WORKING') return 'thinking';
  return 'idle';
}

export function WatchPage({
  agentId,
  threadId,
  isBot = false,
}: {
  agentId: string;
  threadId: string;
  /** Bot page (`/bot/:threadId`). Hides the right sidebar and keeps only Reset. */
  isBot?: boolean;
}) {
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [planDraft, setPlanDraft] = useState<string | null>(null);
  const [dreamFocus, setDreamFocus] = useState<string | null>(null);
  const [operationObjective, setOperationObjective] = useState<string | null>(null);
  // Messages POSTed whose watch echo hasn't landed yet — shown instantly, then
  // reconciled against (and pruned once present in) the transcript.
  const [optimistic, setOptimistic] = useState<OptimisticSend[]>([]);
  // `next-turn` sends held as tray pills until the agent picks them up.
  const [queued, setQueued] = useState<QueuedMessage[]>([]);
  const watch = usePersistentAgentWatch(agentId, threadId);

  // Drag-to-resize the sidebar; width persists across visits.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem('pa-sidebar-width'));
    return saved >= sidebarMin && saved <= sidebarMax ? saved : 380;
  });
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    localStorage.setItem('pa-sidebar-width', String(sidebarWidth));
  }, [sidebarWidth]);

  // Which sidebar tab is showing; persisted so it survives a reload.
  const [tab, setTab] = useState<SidebarTab>(() => {
    const saved = localStorage.getItem('pa-sidebar-tab');
    return sidebarTabs.some((t) => t.id === saved) ? (saved as SidebarTab) : 'activity';
  });
  useEffect(() => {
    localStorage.setItem('pa-sidebar-tab', tab);
  }, [tab]);
  const startResize = (e: ReactMouseEvent) => {
    e.preventDefault();
    setDragging(true);
    const onMove = (ev: MouseEvent) => {
      const rect = bodyRef.current?.getBoundingClientRect();
      if (!rect) return;
      setSidebarWidth(Math.min(sidebarMax, Math.max(sidebarMin, rect.right - ev.clientX)));
    };
    const onUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const remove = useMutation({
    mutationFn: () =>
      client.http.delete(`v1/persistent-agent-groups/${agentId}/agents/${threadId}`),
    onSuccess: () => {
      // Drop this thread's cached detail and mark every group's thread list
      // stale — otherwise the list page renders the deleted thread from cache
      // until its next poll.
      qc.removeQueries({ queryKey: ['persistent-agent-session', threadId] });
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
      return navigate({ to: routes.agentDetail(agentId) });
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/reset`)
        .json<PersistentAgentDetail>(),
    onSuccess: () => {
      setConfirmReset(false);
      setOptimistic([]);
      setQueued([]);
      // The chat bubbles come from the SSE watch reducer, not react-query, and
      // an already-open EventSource has no idea the server just deleted the
      // transcript out from under it. Clear the live watch history too, or the
      // wiped thread keeps rendering its old conversation until a reload.
      // Same pattern as ThreadChatPanel.
      watch.clearHistory();
      fork(qc.invalidateQueries({ queryKey: ['persistent-agent-session', threadId] }));
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
    },
  });

  const rename = useMutation({
    mutationFn: (name: string | null) =>
      client.http
        .patch(`v1/persistent-agent-groups/${agentId}/agents/${threadId}`, { json: { name } })
        .json<PersistentAgentDetail>(),
    onSuccess: () => {
      setShowRename(false);
      fork(qc.invalidateQueries({ queryKey: ['persistent-agent-session', threadId] }));
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
    },
  });

  const sessionQuery = useQuery({
    queryKey: ['persistent-agent-session', threadId],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${agentId}/agents/${threadId}`)
        .json<PersistentAgentDetail>(),
    refetchInterval: 10_000,
  });

  const session = sessionQuery.data;

  // LEGACY compaction markers for the timeline. The hand-rolled compactor is
  // gone (Mastra Observational Memory compresses context now), so this history
  // is frozen — threads compacted before the switch keep their markers; no new
  // rows ever appear. One fetch, no polling.
  const compactionsQuery = useQuery({
    queryKey: ['persistent-agent-compactions', threadId],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/compactions?limit=50`)
        .json<{ items: CompactionWireEntry[] }>(),
    staleTime: Infinity,
  });
  const compactions = useMemo(
    () => toTimelineCompactions(compactionsQuery.data?.items),
    [compactionsQuery.data?.items],
  );

  // The owning persistent agent (group) — its name prefixes the page title
  // ("<agent> / <thread>"). Query key matches the index page's detail query so
  // the cache is shared; the name is stable, so no polling.
  const groupQuery = useQuery({
    queryKey: ['persistent-agents', agentId, 'detail'],
    queryFn: () =>
      client.http.get(`v1/persistent-agent-groups/${agentId}`).json<{ id: string; name: string }>(),
    staleTime: 60_000,
  });
  const groupName = groupQuery.data?.name;

  // Resolve display names for every agent referenced by an `agent:` provenance
  // source (peer-sent events), so chips show the thread's assigned name instead
  // of a raw id. One batched lookup, refreshed only when the set of referenced
  // ids changes.
  const referencedAgentIds = useMemo(
    () =>
      collectAgentSourceIds([
        ...watch.events.map((e) => e.source),
        ...watch.transcript.map((c) => (c.type === 'user' ? c.source : undefined)),
      ]),
    [watch.events, watch.transcript],
  );
  const agentNamesQuery = useQuery({
    queryKey: ['persistent-agent-names', referencedAgentIds],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-groups/agent-names', {
          searchParams: { ids: referencedAgentIds.join(',') },
        })
        .json<PersistentAgentNamesResponse>(),
    enabled: referencedAgentIds.length > 0,
    staleTime: 60_000,
  });
  const agentNames = agentNamesQuery.data;

  const status = resolvedThreadStatus(watch.status, session?.status ?? null);
  const activity: { text: string; at: string } | null =
    watch.activity ??
    (session?.statusText
      ? {
          text: session.statusText,
          at: new Date(session.statusTextAt ?? session.startedAt).toISOString(),
        }
      : null);

  // The agent's self-reported progress bar (set_progress tool), riding the
  // generic `progress` fact lane — parsed here once and reused by both the
  // pinned sidebar strip and the State Signals tab.
  const progressBar: PersistentAgentProgressBar | null = useMemo(() => {
    const parsed = PersistentAgentProgressFactSchema.safeParse(watch.facts.progress?.data);
    return parsed.success ? parsed.data.progress : null;
  }, [watch.facts.progress]);

  // Mark optimistic sends whose echo has landed as `settled` (each delivered
  // `user` chunk consumes one matching optimistic). Settled entries stay in the
  // list so `mergeOptimistic` can keep re-keying the delivered bubble to the
  // optimistic key — dropping them used to flip the React key and remount
  // (flash). Failed sends stay until retried. Cap settled history so a long
  // session doesn't grow unbounded.
  useEffect(() => {
    setOptimistic((prev) => {
      if (prev.length === 0) return prev;
      const counts = new Map<string, number>();
      for (const chunk of watch.transcript) {
        if (chunk.type === 'user') {
          const key = chunk.text.trim();
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }
      }
      let changed = false;
      const remaining: OptimisticSend[] = [];
      for (const send of prev) {
        if (send.failed) {
          remaining.push(send);
          continue;
        }
        const key = send.text.trim();
        const n = counts.get(key) ?? 0;
        if (send.settled) {
          // Settled entries still consume a transcript slot so a later pending
          // of the same text can't falsely settle against an already-claimed
          // echo. Drop ones whose echo fell off the transcript cap.
          if (n > 0) {
            counts.set(key, n - 1);
            remaining.push(send);
          } else {
            changed = true;
          }
          continue;
        }
        if (n > 0) {
          counts.set(key, n - 1);
          remaining.push({ ...send, settled: true });
          changed = true;
        } else {
          remaining.push(send);
        }
      }
      // Keep the newest 80 settled/pending entries; failed always kept.
      const failed = remaining.filter((s) => s.failed);
      const live = remaining.filter((s) => !s.failed);
      const capped = live.length > 80 ? live.slice(live.length - 80) : live;
      if (capped.length !== live.length) changed = true;
      const next = [...capped, ...failed];
      return changed || next.length !== prev.length ? next : prev;
    });
  }, [watch.transcript]);

  // Promote the oldest queued pill when the agent starts a run (IDLE→WORKING is
  // when it drains the next-turn queue) — dropping it from `queued` un-suppresses
  // its inline echo, so it "drops into" the conversation. Heuristic pickup: we
  // don't have a per-message picked-up signal without eventId.
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const was = prevStatusRef.current;
    prevStatusRef.current = status;
    if (was !== 'WORKING' && status === 'WORKING') {
      setQueued((prev) => (prev.length > 0 ? prev.slice(1) : prev));
    }
  }, [status]);

  const postEvent = (
    text: string,
    kind: PersistentAgentEventKind,
    delivery: PersistentAgentEventDelivery,
    localId: string,
  ) => {
    fork(
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/events`, {
          json: { kind, delivery, content: text },
        })
        .json()
        .then(
          () => {
            // Leave it in the list; the prune effect removes it once the
            // transcript echo lands (keeps the delivery chip visible meanwhile).
          },
          () => {
            setOptimistic((prev) =>
              prev.map((s) => (s.localId === localId ? { ...s, failed: true } : s)),
            );
          },
        ),
    );
  };

  const handleSend = (
    text: string,
    kind: 'instruction' | 'notification' | 'completion',
    delivery: PersistentAgentEventDelivery,
  ) => {
    const localId = `s${(localSendSeq += 1)}`;
    // `next-turn` deliveries wait behind the current run — hold them in the
    // queued tray (suppressed inline) rather than showing a pending bubble.
    if (delivery === 'next-turn') {
      setQueued((prev) => [...prev, { localId, text }]);
      fork(
        client.http
          .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/events`, {
            json: { kind, delivery, content: text },
          })
          .json()
          .then(
            () => {},
            () =>
              setQueued((prev) =>
                prev.map((q) => (q.localId === localId ? { ...q, failed: true } : q)),
              ),
          ),
      );
      // Failsafe: never let a pill stick if we miss the pickup signal.
      setTimeout(() => setQueued((prev) => prev.filter((q) => q.localId !== localId)), 45_000);
      return;
    }
    setOptimistic((prev) => [
      ...prev,
      { localId, text, kind, delivery, wasWorking: status === 'WORKING' },
    ]);
    postEvent(text, kind, delivery, localId);
  };

  const handleRetry = (key: string) => {
    const localId = key.replace(/^optimistic-/, '');
    setOptimistic((prev) => {
      const target = prev.find((s) => s.localId === localId);
      if (!target) return prev;
      postEvent(target.text, target.kind, target.delivery, localId);
      return prev.map((s) => (s.localId === localId ? { ...s, failed: false } : s));
    });
  };

  const retryQueued = (localId: string) => {
    setQueued((prev) => {
      const target = prev.find((q) => q.localId === localId);
      if (!target) return prev;
      fork(
        client.http
          .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/events`, {
            json: { kind: 'instruction', delivery: 'next-turn', content: target.text },
          })
          .json()
          .then(
            () => {},
            () =>
              setQueued((q) => q.map((e) => (e.localId === localId ? { ...e, failed: true } : e))),
          ),
      );
      return prev.map((q) => (q.localId === localId ? { ...q, failed: false } : q));
    });
  };

  // Memoized so unrelated re-renders (scroll pin, composer state, token
  // refresh) don't rebuild the whole timeline; only transcript/event/status
  // changes do. Combined with the per-item React.memo, a streaming batch then
  // re-renders just the trailing turn instead of every bubble.
  const items = useMemo(
    () =>
      buildTimeline({
        transcript: watch.transcript,
        optimistic,
        events: watch.events,
        queued: queued.map((q) => q.text),
        compactions,
        status,
      }),
    [watch.transcript, optimistic, watch.events, queued, compactions, status],
  );
  const composerDisabled = status === 'WAITING_FOR_INPUT';
  // `/bot/:id` passes isBot. The same thread opened under the agents URL
  // should drop the same chrome, including the "Agent › name" breadcrumb.
  const botChrome = isBot || agentId === formantBotAgentId;
  const voice = useBotVoice(threadId);
  const botsQuery = useQuery({
    queryKey: ['formant-bots'],
    enabled: botChrome,
    queryFn: () => client.http.get('v1/formant-bots').json<FormantBot[]>(),
  });
  const openBot = botChrome
    ? botsQuery.data?.find((bot) => bot.persistentAgentId === threadId)
    : undefined;
  // Same seed as the sidebar (the bot id). Seeding from the thread id drew a
  // different face whenever a saved avatar was missing or still in the old shape.
  const botAvatar = openBot ? resolveBotAvatar(openBot.avatar, openBot.id) : null;
  // Opening and leaving both count as a look. The sidebar hides the dot only while
  // the row is active, so drop this bot from the unseen cache immediately or the
  // dot returns the moment another bot is opened.
  useEffect(() => {
    if (!botChrome) return;
    const thread = threadId;
    const drop = () => {
      const botId = qc
        .getQueryData<FormantBot[]>(['formant-bots'])
        ?.find((bot) => bot.persistentAgentId === thread)?.id;
      if (!botId) return;
      qc.setQueryData<FormantBotUnseen>(['formant-bots-unseen'], (prev) =>
        prev ? { botIds: prev.botIds.filter((id) => id !== botId) } : prev,
      );
    };
    const stamp = () => {
      drop();
      client.http
        .post(`v1/formant-bots/threads/${thread}/seen`, { json: {} })
        .catch(() => undefined);
    };
    stamp();
    return () => stamp();
  }, [botChrome, threadId, client, qc]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {!botChrome ? (
        <nav aria-label="Breadcrumb" className="bc" style={{ marginBottom: 'var(--sp-3)' }}>
          <Link to={routes.agentDetail(agentId)}>{groupName ?? 'Agent'}</Link>
          <span className="sep" aria-hidden="true">
            ›
          </span>
          <span className="item is-leaf">{session?.name ?? `Thread ${threadId.slice(0, 8)}`}</span>
        </nav>
      ) : null}
      <PageHeader
        title={
          botChrome
            ? (session?.name ?? 'Bot')
            : `${groupName ? `${groupName} / ` : ''}${session?.name ?? `Thread ${threadId.slice(0, 8)}`}`
        }
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!botChrome ? (
              <BackButton to={routes.agentDetail(agentId)} label={groupName ?? 'Agent'} />
            ) : null}
            {!botChrome && status ? (
              <span className={statusBadge[status] ?? 'badge badge--neutral'}>{status}</span>
            ) : null}
            {!botChrome && !status ? (
              <span className="badge badge--neutral">connecting…</span>
            ) : null}
            {!botChrome && session ? (
              <span className="t-xs t-muted" title="Total LLM cost for this session">
                {formatUsd(session.totalCostUsd)}
              </span>
            ) : null}
            {!botChrome ? (
              <button
                className="btn btn--secondary btn--sm"
                disabled={rename.isPending}
                title="Rename this thread (a label only — nothing re-addresses)"
                onClick={() => {
                  setRenameValue(session?.name ?? '');
                  rename.reset();
                  setShowRename(true);
                }}
              >
                Rename
              </button>
            ) : null}
            <button
              className="btn btn--secondary btn--sm"
              disabled={reset.isPending}
              title="Wipe this thread and rebuild it from the agent's current definition — cannot be undone"
              onClick={() => setConfirmReset(true)}
            >
              {reset.isPending ? 'Resetting…' : 'Reset'}
            </button>
            {!botChrome ? (
              <button
                className="btn btn--destructive btn--sm"
                disabled={remove.isPending}
                title="Delete this thread and its history — cannot be undone"
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </button>
            ) : null}
          </div>
        }
      />

      <Modal
        open={showRename}
        onClose={() => setShowRename(false)}
        eyebrow={`Thread ${threadId.slice(0, 8)}`}
        title="Rename thread"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button className="btn btn--ghost btn--sm" onClick={() => setShowRename(false)}>
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={rename.isPending}
              onClick={() => rename.mutate(renameValue.trim() || null)}
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              placeholder="e.g. East field — night shift"
              value={renameValue}
              maxLength={200}
              autoFocus={true}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !rename.isPending)
                  rename.mutate(renameValue.trim() || null);
              }}
            />
          </label>
          <p className="t-xs t-muted" style={{ margin: 0 }}>
            A label for people only — the thread id stays the same. Leave empty to clear the name.
          </p>
          {rename.isError && (
            <div className="alert alert--danger">
              {String((rename.error as Error)?.message ?? 'Failed to rename thread')}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => reset.mutate()}
        eyebrow={session?.name ?? `Thread ${threadId.slice(0, 8)}`}
        title={threadResetConfirmTitle}
        message={threadResetConfirmMessage}
        confirmLabel={threadResetConfirmLabel}
        destructive={true}
        pending={reset.isPending}
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        eyebrow={session?.name ?? `Thread ${threadId.slice(0, 8)}`}
        title="Delete this thread?"
        message="This permanently removes the thread along with its messages, state signals, and transcript. This cannot be undone."
        confirmLabel="Delete"
        destructive={true}
        pending={remove.isPending}
      />

      {planDraft !== null && (
        <PlanProposalModal
          agentId={agentId}
          threadId={threadId}
          initialObjective={planDraft}
          onClose={() => setPlanDraft(null)}
          onApproved={() => {
            setPlanDraft(null);
            setTab('mission');
          }}
        />
      )}
      {dreamFocus !== null && (
        <DreamModal
          agentId={agentId}
          threadId={threadId}
          focus={dreamFocus}
          onClose={() => setDreamFocus(null)}
        />
      )}
      {operationObjective !== null && (
        <OperationStartModal
          threadId={threadId}
          initialObjective={operationObjective}
          onClose={() => setOperationObjective(null)}
          onStarted={() => {
            setOperationObjective(null);
            setTab('operation');
          }}
        />
      )}

      {/* Body: chat column + a drag-resizable sidebar, bounded to the height. */}
      <div ref={bodyRef} style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'visible' }}>
        <section
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            flexDirection: 'column',
            minHeight: 0,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--surface)',
            // Keep overflow visible so the composer slash menu can open upward
            // without the top of the popover being clipped by this column.
            overflow: 'visible',
          }}
        >
          {/* Clip only the transcript + tray; composer sits outside this box. */}
          <div
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
            }}
          >
            <ConsoleAgentChat
              key={threadId}
              groupId={agentId}
              persistentAgentId={threadId}
              items={items}
              status={status}
              activity={activity}
              connection={watch.connection}
              loading={!watch.hasLoaded}
              emptyMark={
                botAvatar ? (
                  <BotAvatar
                    avatar={botAvatar}
                    size={128}
                    state={botFaceState(status, botChrome ? voice : null)}
                    title="Bot avatar"
                  />
                ) : undefined
              }
              onRetry={handleRetry}
              agentNames={agentNames}
            />
            <QueuedTray items={queued} onRetry={retryQueued} />
          </div>
          <ChatComposer
            status={status}
            onSend={handleSend}
            onCommand={(command, args) => {
              if (command === 'plan') setPlanDraft(args);
              else if (command === 'dream') setDreamFocus(args);
              else setOperationObjective(args);
            }}
            disabled={composerDisabled}
            isBot={botChrome}
            voice={botChrome ? voice : null}
          />
        </section>

        {!botChrome ? (
          <>
            <div
              className={`pa-sidebar-resizer${dragging ? ' is-dragging' : ''}`}
              onMouseDown={startResize}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
            />

            <aside
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-3)',
                width: sidebarWidth,
                flexShrink: 0,
                minHeight: 0,
              }}
            >
              {/* Pinned status + lifecycle — always glanceable above the tabs. */}
              <SidebarStatusBar
                agentId={agentId}
                threadId={threadId}
                status={status}
                progressBar={progressBar}
              />

              <div className="pa-sidebar-tabs" role="tablist">
                {sidebarTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`pa-sidebar-tab${tab === t.id ? ' is-active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* The active tab owns the full remaining height and scrolls itself. */}
              <div className="pa-sidebar-tabpanel" role="tabpanel">
                {tab === 'activity' && <EventsFeed events={watch.events} agentNames={agentNames} />}
                {tab === 'operation' && (
                  <OperationPanel fact={watch.facts.operation_state ?? null} />
                )}
                {tab === 'state' && (
                  <StatePanel
                    agentId={agentId}
                    threadId={threadId}
                    facts={Object.values(watch.facts)}
                    terminal={status === 'STOPPED' || status === 'FAILED'}
                  />
                )}
                {tab === 'tools' && (
                  <div style={{ overflowX: 'auto' }}>
                    {/* fillHeight → the tab panel is the single scroller (no nested scrollbar). */}
                    <ToolUsagePanel agentId={agentId} threadId={threadId} fillHeight={true} />
                  </div>
                )}
                {tab === 'heartbeat' && (
                  <HeartbeatPanel
                    agentId={agentId}
                    threadId={threadId}
                    heartbeats={session?.heartbeats ?? []}
                    status={status}
                  />
                )}
                {tab === 'signals' && (
                  <SignalsPanel agentId={agentId} threadId={threadId} status={status} />
                )}
                {tab === 'mission' && (
                  <MissionPanel
                    facts={Object.values(watch.facts)}
                    agentId={agentId}
                    threadId={threadId}
                  />
                )}
                {tab === 'metrics' && (
                  <MetricsPanel
                    agentId={agentId}
                    threadId={threadId}
                    liveTurns={Object.values(watch.turns)}
                  />
                )}
              </div>
            </aside>
          </>
        ) : null}
      </div>
    </div>
  );
}

function operationFailureSummary(failure: unknown): string | null {
  if (failure == null) return null;
  if (typeof failure === 'string') return failure;
  if (typeof failure === 'object') {
    const record = failure as Record<string, unknown>;
    for (const key of ['reason', 'message', 'summary']) {
      if (typeof record[key] === 'string') return record[key];
    }
  }
  try {
    return JSON.stringify(failure);
  } catch {
    return String(failure);
  }
}

function OperationPanel({ fact }: { fact: IWatchFact | null }) {
  const client = useClient();
  const parsed = PersistentAgentOperationStateProjectionSchema.safeParse(fact?.data);
  const lifecycle = useMutation({
    mutationFn: (input: {
      operationId: string;
      expectedRevision: number;
      action: 'pause' | 'resume' | 'stop';
    }) =>
      client.http
        .post(`v1/persistent-agent-operations/${input.operationId}/lifecycle`, {
          json: {
            expectedRevision: input.expectedRevision,
            action: input.action,
          },
        })
        .json(),
  });
  const message = useMutation({
    mutationFn: (input: {
      operationId: string;
      workerId: string;
      content: string;
      interrupt: boolean;
    }) =>
      client.http
        .post(`v1/persistent-agent-operations/${input.operationId}/messages`, {
          json: {
            workerIds: [input.workerId],
            content: input.content,
            delivery: input.interrupt ? 'interrupt' : 'when-idle',
          },
        })
        .json(),
  });
  if (!parsed.success) {
    return (
      <div className="empty-state">
        <p>No supervisor operation is attached to this thread.</p>
        <p className="muted t-xs">
          A supervisor with a manageWorkers grant can start one from chat; its continuous worker
          team will appear here.
        </p>
      </div>
    );
  }
  const state = parsed.data;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <section>
        <div className="flex items-center justify-between">
          <strong>{state.operation.status}</strong>
          <span className="muted t-xs">revision {state.operation.revision}</span>
        </div>
        <div className="flex items-center" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {state.operation.status === 'ACTIVE' && (
            <button
              className="btn btn--secondary btn--sm"
              disabled={lifecycle.isPending}
              onClick={() =>
                lifecycle.mutate({
                  operationId: state.operation.id,
                  expectedRevision: state.operation.revision,
                  action: 'pause',
                })
              }
            >
              Pause
            </button>
          )}
          {state.operation.status === 'PAUSED' && (
            <button
              className="btn btn--secondary btn--sm"
              disabled={lifecycle.isPending}
              onClick={() =>
                lifecycle.mutate({
                  operationId: state.operation.id,
                  expectedRevision: state.operation.revision,
                  action: 'resume',
                })
              }
            >
              Resume
            </button>
          )}
          {!['STOPPED', 'COMPLETED', 'FAILED'].includes(state.operation.status) && (
            <button
              className="btn btn--danger btn--sm"
              disabled={lifecycle.isPending}
              onClick={() => {
                if (!window.confirm('Stop this operation and retire all of its workers?')) return;
                lifecycle.mutate({
                  operationId: state.operation.id,
                  expectedRevision: state.operation.revision,
                  action: 'stop',
                });
              }}
            >
              Stop
            </button>
          )}
        </div>
        {lifecycle.error && <p className="text-danger t-xs">{String(lifecycle.error)}</p>}
        <p className="t-sm" style={{ whiteSpace: 'pre-wrap' }}>
          {state.operation.objective}
        </p>
        {state.operation.strategy && (
          <p className="muted t-xs" style={{ whiteSpace: 'pre-wrap' }}>
            {state.operation.strategy}
          </p>
        )}
      </section>

      <section>
        <strong className="t-sm">Workers ({state.workers.length})</strong>
        <div style={{ display: 'grid', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
          {state.workers.map((worker) => {
            const failure = operationFailureSummary(worker.failure);
            return (
              <div key={worker.id} className="card" style={{ padding: 'var(--sp-3)' }}>
                <div className="flex items-center justify-between">
                  <span className="t-xs" title={worker.persistentAgentId}>
                    {worker.persistentAgentId.slice(0, 12)}
                  </span>
                  <span
                    className={
                      worker.status === 'FAILED'
                        ? 'badge badge--danger'
                        : worker.status === 'WORKING'
                          ? 'badge badge--success'
                          : 'badge badge--neutral'
                    }
                  >
                    {worker.status}
                  </span>
                </div>
                <div className="t-xs" style={{ whiteSpace: 'pre-wrap' }}>
                  {worker.scope}
                </div>
                {worker.lastActivityAt && (
                  <div className="muted t-xs">
                    last active {formatRelative(worker.lastActivityAt)}
                  </div>
                )}
                {failure && <div className="text-danger t-xs">failure: {failure}</div>}
                {worker.lastTool && <div className="muted t-xs">last tool: {worker.lastTool}</div>}
                {!['STOPPED', 'FAILED'].includes(worker.status) && (
                  <div className="flex items-center" style={{ gap: 'var(--sp-2)' }}>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={message.isPending}
                      onClick={() => {
                        const content = window.prompt('Follow up with this worker');
                        if (!content) return;
                        message.mutate({
                          operationId: state.operation.id,
                          workerId: worker.id,
                          content,
                          interrupt: false,
                        });
                      }}
                    >
                      Follow up
                    </button>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={message.isPending}
                      onClick={() => {
                        const content = window.prompt('Interrupt and redirect this worker');
                        if (!content) return;
                        message.mutate({
                          operationId: state.operation.id,
                          workerId: worker.id,
                          content,
                          interrupt: true,
                        });
                      }}
                    >
                      Interrupt
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {state.scorecard !== undefined && (
        <section>
          <strong className="t-sm">Score feedback</strong>
          <pre className="t-xs" style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(state.scorecard, null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}

/** Pinned status strip above the tabs — the current status + the always-useful
 *  lifecycle controls (Pause / Resume / Stop) in one compact row. */
function SidebarStatusBar({
  agentId,
  threadId,
  status,
  progressBar,
}: {
  agentId: string;
  threadId: string;
  status: PersistentAgentStatus | null;
  progressBar: PersistentAgentProgressBar | null;
}) {
  const client = useClient();
  const qc = useQueryClient();
  const [optimisticStatus, setOptimisticStatus] = useState<PersistentAgentStatus | null>(null);
  const lifecycle = useMutation({
    mutationFn: (action: ThreadLifecycleAction) =>
      client.http
        .post(threadLifecyclePath(agentId, threadId, action))
        .json<{ status: PersistentAgentStatus }>(),
    onSuccess: (updated) => {
      // The REST write is authoritative. The host's SSE status frame may lag
      // (or never arrive if stop tears ownership down first), so reflect the
      // returned lifecycle immediately and refresh every cached list/detail.
      setOptimisticStatus(updated.status);
      qc.setQueryData<PersistentAgentDetail>(['persistent-agent-session', threadId], (current) =>
        current
          ? { ...current, status: updated.status as PersistentAgentDetail['status'] }
          : current,
      );
      fork(qc.invalidateQueries({ queryKey: ['persistent-agent-session', threadId] }));
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
    },
  });

  useEffect(() => {
    if (optimisticStatus !== null && status === optimisticStatus) setOptimisticStatus(null);
  }, [optimisticStatus, status]);

  const shownStatus = displayedThreadStatus(status, optimisticStatus);
  const terminal = shownStatus === 'STOPPED' || shownStatus === 'FAILED';
  const canPause =
    shownStatus === 'STARTING' || shownStatus === 'IDLE' || shownStatus === 'WORKING';
  const canResume = shownStatus === 'PAUSED';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="pa-sidebar-status">
        <span className={statusBadge[shownStatus ?? ''] ?? 'badge badge--neutral'}>
          {shownStatus ?? '—'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {terminal ? (
            <span className="pa-sidebar-status__meta">
              {shownStatus === 'FAILED' ? 'stopped after an error' : 'stopped — can’t resume'}
            </span>
          ) : (
            <>
              {canPause && (
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={lifecycle.isPending}
                  title="Stop reacting to messages until resumed (keeps memory + state signals)"
                  onClick={() => lifecycle.mutate('pause')}
                >
                  Pause
                </button>
              )}
              {canResume && (
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={lifecycle.isPending}
                  title="Start reacting to messages again"
                  onClick={() => lifecycle.mutate('resume')}
                >
                  Resume
                </button>
              )}
              <button
                className="btn btn--destructive btn--sm"
                disabled={lifecycle.isPending}
                title="End this thread for good — this cannot be undone"
                onClick={() => lifecycle.mutate('stop')}
              >
                Stop
              </button>
            </>
          )}
        </div>
      </div>
      {lifecycle.error && (
        <div className="alert alert--danger" role="alert">
          Failed to change thread status: {(lifecycle.error as Error).message}
        </div>
      )}
      {progressBar && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ProgressBar bar={progressBar} />
        </div>
      )}
    </div>
  );
}

/** State Signals tab — the fact dashboard + a lane setter. */
function StatePanel({
  agentId,
  threadId,
  facts,
  terminal,
}: {
  agentId: string;
  threadId: string;
  facts: IWatchFact[];
  terminal: boolean;
}) {
  const client = useClient();
  const [laneId, setLaneId] = useState('');
  const [factValue, setFactValue] = useState('');

  const setFact = useMutation({
    mutationFn: () =>
      client.http
        .put(
          `v1/persistent-agent-groups/${agentId}/agents/${threadId}/facts/${encodeURIComponent(laneId.trim())}`,
          { json: { data: factValue.trim(), summary: factValue.trim() } },
        )
        .json(),
    onSuccess: () => setFactValue(''),
  });

  return (
    <>
      {facts.length === 0 ? (
        <span className="t-muted t-sm">No state signals yet.</span>
      ) : (
        facts.map((f) => {
          if (f.laneId === 'tasks') return <TaskListFact key={f.laneId} fact={f} />;
          if (f.laneId === 'progress') return <ProgressBarsFact key={f.laneId} fact={f} />;
          if (f.laneId === 'learnings') return <LearningsFact key={f.laneId} fact={f} />;
          if (f.laneId === 'working_memory') return <WorkingMemoryFact key={f.laneId} fact={f} />;
          if (f.laneId === 'observational_memory')
            return <ObservationalMemoryFact key={f.laneId} fact={f} />;
          return <FactRow key={f.laneId} fact={f} />;
        })
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input
          className="input"
          placeholder="Lane"
          value={laneId}
          onChange={(e) => setLaneId(e.target.value)}
          disabled={terminal}
          style={{ width: 90 }}
        />
        <input
          className="input"
          placeholder="Value…"
          value={factValue}
          onChange={(e) => setFactValue(e.target.value)}
          disabled={terminal}
          style={{ flex: 1, minWidth: 0 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && laneId.trim() && factValue.trim() && !terminal)
              setFact.mutate();
          }}
        />
        <button
          className="btn btn--secondary btn--sm"
          disabled={!laneId.trim() || !factValue.trim() || setFact.isPending || terminal}
          onClick={() => setFact.mutate()}
        >
          Set
        </button>
      </div>
      <p className="t-xs t-muted" style={{ marginTop: 6, marginBottom: 0 }}>
        The current value of a named lane — updates the dashboard in place, doesn’t wake the thread.
      </p>
    </>
  );
}

function FactRow({ fact }: { fact: IWatchFact }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="badge badge--neutral t-mono">{fact.laneId}</span>
        <span className="t-xs t-muted">{formatRelative(fact.date)}</span>
      </div>
      <div className="t-sm" style={{ marginTop: 4 }}>
        {fact.summary || <span className="t-muted">(no summary)</span>}
      </div>
    </div>
  );
}

interface ITaskItem {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface ITaskListData {
  items: ITaskItem[];
  done: number;
  total: number;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isTaskItem(value: unknown): value is ITaskItem {
  return (
    isPlainRecord(value) &&
    typeof value.content === 'string' &&
    (value.status === 'pending' || value.status === 'in_progress' || value.status === 'completed')
  );
}

function isTaskListData(data: unknown): data is ITaskListData {
  return (
    isPlainRecord(data) &&
    Array.isArray(data.items) &&
    data.items.every(isTaskItem) &&
    typeof data.done === 'number' &&
    typeof data.total === 'number'
  );
}

/** Claude-Code-style checkbox list for the `tasks` fact lane; falls back to FactRow when `data` doesn't match. */
function TaskListFact({ fact }: { fact: IWatchFact }) {
  if (!isTaskListData(fact.data)) return <FactRow fact={fact} />;
  const { items, done, total } = fact.data;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="badge badge--neutral t-mono">tasks</span>
        <span className="t-xs t-muted">{formatRelative(fact.date)}</span>
      </div>
      <div className="t-xs t-muted" style={{ marginTop: 4 }}>
        {done}/{total} done
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: '6px 0 0',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {items.map((item, i) => (
          <li key={i} className="t-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <TaskCheckMark status={item.status} />
            <span
              style={{
                textDecoration: item.status === 'completed' ? 'line-through' : 'none',
                color: item.status === 'completed' ? 'var(--text-muted)' : undefined,
              }}
            >
              {item.content}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The `progress` fact lane (set_progress/clear_progress tools) as a tick row. */
function ProgressBarsFact({ fact }: { fact: IWatchFact }) {
  const parsed = PersistentAgentProgressFactSchema.safeParse(fact.data);
  if (!parsed.success || !parsed.data.progress) return <FactRow fact={fact} />;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="badge badge--neutral t-mono">progress</span>
        <span className="t-xs t-muted">{formatRelative(fact.date)}</span>
      </div>
      <div style={{ marginTop: 6 }}>
        <ProgressBar bar={parsed.data.progress} />
      </div>
    </div>
  );
}

function TaskCheckMark({ status }: { status: ITaskItem['status'] }) {
  if (status === 'completed')
    return (
      <span aria-hidden={true} style={{ color: 'var(--success, #1f9d55)' }}>
        ☑
      </span>
    );
  if (status === 'in_progress')
    return (
      <span aria-hidden={true} style={{ color: 'var(--accent-primary, #6ea8ff)' }}>
        ◐
      </span>
    );
  return (
    <span aria-hidden={true} style={{ color: 'var(--text-muted)' }}>
      ☐
    </span>
  );
}

interface ILearningItem {
  objective: string;
  reason: string;
  at: string;
  count?: number;
}

interface ILearningsData {
  items: ILearningItem[];
}

function isLearningItem(value: unknown): value is ILearningItem {
  return (
    isPlainRecord(value) &&
    typeof value.objective === 'string' &&
    typeof value.reason === 'string' &&
    typeof value.at === 'string' &&
    (value.count === undefined || typeof value.count === 'number')
  );
}

function isLearningsData(data: unknown): data is ILearningsData {
  return isPlainRecord(data) && Array.isArray(data.items) && data.items.every(isLearningItem);
}

/** Objective-failure lessons the agent has learned; falls back to FactRow when `data` doesn't match. */
function LearningsFact({ fact }: { fact: IWatchFact }) {
  if (!isLearningsData(fact.data)) return <FactRow fact={fact} />;
  const { items } = fact.data;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="badge badge--neutral t-mono">learnings</span>
        <span className="t-xs t-muted">{formatRelative(fact.date)}</span>
      </div>
      <div className="t-xs t-muted" style={{ marginTop: 4 }}>
        Lessons from objectives that failed or needed correction.
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: '6px 0 0',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {items.map((item, i) => (
          <li key={i} className="t-sm">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500 }}>{item.objective}</span>
              {item.count && item.count > 1 && (
                <span className="badge badge--outline t-mono" style={{ fontSize: 9 }}>
                  ×{item.count}
                </span>
              )}
              <span className="t-xs t-muted" style={{ marginLeft: 'auto' }}>
                {formatRelative(item.at)}
              </span>
            </div>
            <div className="t-xs t-muted" style={{ marginTop: 2 }}>
              {item.reason}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface IObservedPattern {
  what: string;
  period_sec?: number;
  next_expected_sec?: number;
}

interface IWorkingMemoryData {
  current_task: string | null;
  learnings: string[];
  /** Absent on facts written before the Observer tracked recurrences. */
  observed_patterns?: IObservedPattern[];
}

export function isObservedPatternArray(value: unknown): value is IObservedPattern[] {
  return (
    Array.isArray(value) &&
    value.every((entry) => isPlainRecord(entry) && typeof entry.what === 'string')
  );
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

export function isWorkingMemoryData(data: unknown): data is IWorkingMemoryData {
  return (
    isPlainRecord(data) &&
    (data.current_task === null || typeof data.current_task === 'string') &&
    isStringArray(data.learnings) &&
    // Optional: facts persisted before this field existed must keep rendering
    // as working memory rather than silently degrading to a raw FactRow.
    (data.observed_patterns === undefined || isObservedPatternArray(data.observed_patterns))
  );
}

/**
 * One recurrence as a single line: what repeats, then the timing when the
 * Observer managed to pin it down. The cadence is the whole point of the
 * entry, so it reads after an em dash rather than being buried in the prose.
 */
export function formatObservedPattern(pattern: IObservedPattern): string {
  const timing = [
    pattern.period_sec === undefined ? null : `every ${pattern.period_sec}s`,
    pattern.next_expected_sec === undefined ? null : `next ~t+${pattern.next_expected_sec}s`,
  ].filter((part): part is string => part !== null);
  return timing.length > 0 ? `${pattern.what} — ${timing.join(', ')}` : pattern.what;
}

/** The Observer-maintained working memory (current task + learnings + recurring patterns); falls back to FactRow when `data` doesn't match. */
export function WorkingMemoryFact({ fact }: { fact: IWatchFact }) {
  if (!isWorkingMemoryData(fact.data)) return <FactRow fact={fact} />;
  const { current_task: currentTask, learnings, observed_patterns: patterns } = fact.data;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="badge badge--neutral t-mono">working memory</span>
        <span className="t-xs t-muted">{formatRelative(fact.date)}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
        {currentTask && (
          <div>
            <div className="t-xs t-muted" style={{ fontWeight: 500 }}>
              Current task
            </div>
            <div className="t-sm" style={{ marginTop: 2 }}>
              {currentTask}
            </div>
          </div>
        )}
        {learnings.length > 0 && (
          <WorkingMemoryList title="Learnings (observed)" items={learnings} />
        )}
        {patterns !== undefined && patterns.length > 0 && (
          <WorkingMemoryList
            title="Recurring patterns"
            items={patterns.map(formatObservedPattern)}
          />
        )}
      </div>
    </div>
  );
}

function WorkingMemoryList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <div className="t-xs t-muted" style={{ fontWeight: 500 }}>
        {title}
      </div>
      <ul
        style={{
          listStyle: 'disc',
          margin: '2px 0 0',
          paddingLeft: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {items.map((item, i) => (
          <li key={i} className="t-sm">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface IObservationalMemoryData {
  text: string;
  generation: number;
  at: string;
}

function isObservationalMemoryData(data: unknown): data is IObservationalMemoryData {
  return (
    isPlainRecord(data) &&
    typeof data.text === 'string' &&
    typeof data.generation === 'number' &&
    typeof data.at === 'string'
  );
}

/** The Observer's narrative log; falls back to FactRow when `data` doesn't match. */
function ObservationalMemoryFact({ fact }: { fact: IWatchFact }) {
  if (!isObservationalMemoryData(fact.data)) return <FactRow fact={fact} />;
  const { text, generation } = fact.data;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="badge badge--neutral t-mono">observational memory</span>
        <span className="t-xs t-muted">{formatRelative(fact.date)}</span>
      </div>
      <div style={{ marginTop: 4 }}>
        <span className="badge badge--outline t-mono" style={{ fontSize: 9 }}>
          gen {generation}
        </span>
      </div>
      <pre
        className="t-xs t-mono"
        style={{
          margin: '6px 0 0',
          maxHeight: 220,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 'var(--radius-sm, 3px)',
          padding: 8,
        }}
      >
        {text}
      </pre>
    </div>
  );
}

/** The doorbell feed — newest first, capped with a "Show more" reveal. */
function EventsFeed({
  events,
  agentNames,
}: {
  events: IWatchEvent[];
  agentNames?: PersistentAgentNamesResponse;
}) {
  const pageSize = 8;
  const [visible, setVisible] = useState(pageSize);

  if (events.length === 0) {
    return <span className="t-muted t-sm">Nothing has rung yet.</span>;
  }

  const shown = events.slice(0, visible);
  const remaining = events.length - shown.length;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((e, i) => (
          <EventRow key={`${e.at}-${i}`} event={e} agentNames={agentNames} />
        ))}
      </div>
      {remaining > 0 && (
        <button
          className="btn btn--ghost btn--sm"
          style={{ marginTop: 10 }}
          onClick={() => setVisible((v) => v + pageSize)}
        >
          Show {Math.min(pageSize, remaining)} more ({remaining} older)
        </button>
      )}
    </>
  );
}

function EventRow({
  event,
  agentNames,
}: {
  event: IWatchEvent;
  agentNames?: PersistentAgentNamesResponse;
}) {
  return (
    <div
      style={{
        borderLeft: `3px solid ${event.wokeFromIdle ? 'var(--success, #1f9d55)' : 'transparent'}`,
        paddingLeft: 10,
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="badge badge--outline">
          {event.eventKind === 'goal' ? '🎯 goal' : event.eventKind}
        </span>
        <span className="t-xs t-secondary" style={{ wordBreak: 'break-word' }} title={event.source}>
          {sourceLabel(event.source, agentNames)}
        </span>
        {event.wokeFromIdle && <span className="badge badge--success">woke it</span>}
        <span className="t-xs t-muted" style={{ marginLeft: 'auto' }}>
          {formatRelative(event.at)}
        </span>
      </div>
      <div className="t-sm" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        {event.content}
      </div>
    </div>
  );
}
